import { AlertTriangle } from 'lucide-react';

import { Button } from '../../../components/ui/button';

/**
 * Starea de eroare a unui raport — inlocuieste CONTINUTUL, nu se adauga peste el.
 *
 * 🔴 De ce nu un banner deasupra tabelului: cand cererea pica, ecranul nu e doar
 * lipsit de date, ci AFIRMA ceva fals — „TOTAL … 0" si „Nu există date.". Un
 * banner deasupra unei cifre gresite lasa cifra pe ecran. Deci totalul nu se mai
 * randeaza deloc: nici „0", nici „—".
 *
 * De ce SUPRAFATA si nu caseta: e starea unei regiuni, ca „Nu există date.", nu o
 * caseta asezata in pagina. Fara chenar propriu, fara card. Regula
 * „inainte/dupa actiune" pentru chenare (vezi `DeleteConfirmDialog`) NU se aplica
 * aici, fiindca nu e o caseta.
 *
 * ⚠️ Starea de eroare si cea de gol NU au voie sa semene: golul e un rand de text
 * gri, fara pictograma si fara buton. Asta are pictograma rosie, titlu si un
 * buton. Cine le confunda crede ca raportul e gol cand de fapt n-a raspuns nimeni.
 *
 * ⚠️ Cauza NU se numeste. `isError` acopera si 5xx, si transportul mort, si o
 * interogare de editii picata — un text care ar spune „probleme de retea" ar minti
 * in doua cazuri din trei.
 */
interface ReportErrorStateProps {
    /** Reface interogarea. NU reincarca pagina: filtrele si sortarea s-ar pierde. */
    onRetry: () => void;
    /** `false` cat timp reincercarea e in curs, ca sa nu se ceara de doua ori. */
    loading?: boolean;
}

export function ReportErrorState({ onRetry, loading = false }: ReportErrorStateProps) {
    return (
        <div
            role="alert"
            className="flex flex-col items-center justify-center gap-3 px-4 py-10 text-center"
        >
            {/* Pictograma e `aria-hidden` — mesajul e in text. Dar la 360 ea prinde
                ochiul prima, deci culoarea ei e cea care spune „eroare", nu „gol". */}
            <AlertTriangle className="size-8 text-error" aria-hidden />

            <h2 className="font-heading text-large font-semibold text-error m-0">
                Raportul nu s-a putut încărca
            </h2>

            {/* Explicatia e OBLIGATORIE si NU e rosie: rosul ramane pe pictograma si
                pe titlu. Fara ea, „nu s-a putut încărca" nu spune de ce lipseste
                totalul — si tocmai absenta totalului e ce trebuie explicat. */}
            <p className="text-small text-dark m-0 max-w-prose">
                Nu am primit datele, deci nu putem arăta niciun total.
            </p>

            {/* Varianta PRIMARA, nu `outline`. Directia S1: e singura actiune de pe
                ecran, iar pe telefon restul suprafetei e goala — un buton „calm"
                acolo se citeste ca optional, cand de fapt e singura iesire. */}
            <Button onClick={onRetry} disabled={loading} className="mt-1">
                {loading ? 'Se încearcă...' : 'Reîncearcă'}
            </Button>
        </div>
    );
}

/**
 * Fasia pentru cazul in care refetch-ul pica PESTE un raport deja incarcat.
 *
 * Aici ecranul nu minte: cifrele afisate au fost reale la un moment dat. Deci nu
 * se goleste — datele raman, si se spune doar ca pot fi vechi. Golirea ar pierde
 * informatie corecta din cauza unei cereri picate.
 */
export function ReportStaleBar({ onRetry, loading = false }: ReportErrorStateProps) {
    return (
        <div
            role="status"
            /* `border-y border-hairline` peste umplutura, nu o umplutura mai adanca:
               masurat de S1, `bg-error/5` da 1,08 fata de alb — sub delta de
               suprafata a sistemului (1,28), deci fasia nu se citea ca regiune.
               `/10` ar da doar 1,19 si ar apropia-o vizual de o caseta de eroare,
               ceea ce ar fi gresit: aici datele sunt bune, doar vechi. */
            className="flex flex-wrap items-center justify-between gap-2 border-y border-hairline px-4 py-2 bg-error/5"
        >
            <span className="text-small text-dark">Cifrele pot fi neactualizate</span>
            {/* Aici butonul RAMANE secundar, spre deosebire de cel din starea de
                eroare: raportul e pe ecran si utilizabil, iar actiunea principala e
                sa-l citeasca. O fasie discreta cu buton primar ar tipa mai tare
                decat continutul pe care il adnoteaza. */}
            <Button variant="outline" size="sm" onClick={onRetry} disabled={loading}>
                {loading ? 'Se încearcă...' : 'Reîncearcă'}
            </Button>
        </div>
    );
}

/**
 * Starea „interogarea nu s-a incheiat inca" — scheletul.
 *
 * 🔴 Exista pentru o minciuna MAI VECHE decat cea de eroare, si mai frecventa:
 * cu interogarea de editii doar lenta (200 OK in 6 s), raportul afisa secunde
 * intregi „TOTAL … 0" si „Nu există date.", fara niciun indiciu de incarcare.
 * `isError` e fals pe toata durata, deci starea de eroare n-o acopera.
 *
 * Cauza tehnica: interogarea principala e `enabled: !!currentEdition`, iar in
 * React Query v5 o interogare DEZACTIVATA are `isPending: true` si
 * `isFetching: false` — deci `isLoading` iese FALS si suprapunerea de incarcare a
 * layout-ului nu se aprinde.
 *
 * E minimul care nu minte, nu un design nou pe mobil: nicio cifra, si nici
 * „Nu există date." — care ar fi tot o afirmatie despre date pe care nu le avem.
 */
export function ReportPendingState() {
    return (
        <div className="flex flex-col gap-3 px-4 py-6" aria-busy="true" aria-live="polite">
            {/* Textul e pentru cititorul de ecran SI pentru ochi: un schelet mut
                arata ca un layout rupt. */}
            <p className="text-small text-gray-600 m-0">Se încarcă raportul...</p>

            {/* Trei bare, nu un numar variabil: sugereaza „vin randuri", fara sa
                promita cate. `animate-pulse` e utilitar Tailwind, nu stil ad-hoc. */}
            <div aria-hidden="true" className="flex flex-col gap-2">
                <div className="h-11 rounded-md bg-muted animate-pulse" />
                <div className="h-11 rounded-md bg-muted animate-pulse" />
                <div className="h-11 rounded-md bg-muted animate-pulse" />
            </div>
        </div>
    );
}
